import { useState, type ReactNode } from 'react';
import { Button, Card, CardHeader, CardTitle, Field, Select } from '@paige/ui';
import { useFormat } from '@paige/format';
import { compare } from '../period/compare';
import { periodLabel, tileLabel, type PeriodChoice } from '../period/period';
import type { AdminPeriod } from '../period/period-types';
import type { PartnerInvoices, Revenue } from './revenue-types';

const EXPLAINED =
  'Paid by partners on their Paige invoices, for the companies they manage (ADR-0029). One invoice covers many ' +
  'companies and earns no partner share, so it is in no company row and not in Collected by card; Paying counts ' +
  'only companies that paid Ademero themselves.';

const INVOICED =
  'Ademero invoice statements issued in the period, by issue day (ADR-0036): invoiced, not paid, until staff ' +
  'mark a statement paid, when it also counts in Paid by partners.';

type RenderPartner = (zohoAccountId: string, name: string) => ReactNode;

/** The period's partner money, net of refunds, against the period before; and what Ademero invoiced. */
function Figures({ money, days }: { money: PartnerInvoices; days: number }): ReactNode {
  const f = useFormat();
  const compared = compare(money.net, money.previousNet, { days, format: f.money });
  return (
    <div className="ad-rfigs">
      <div className="ad-rfig ad-rfig--lead" title={EXPLAINED}>
        <span className="ad-rfig__label">Paid by partners</span>
        <strong className="ad-rfig__value">{f.money(money.net)}</strong>
        {money.refunded > 0 ? (
          <span className="ad-rfig__note">after {f.money(money.refunded)} refunded</span>
        ) : null}
        <span className="ad-rfig__compare">
          <span className={`ad-tone--${compared.tone}`}>{compared.change}</span> {compared.before}
        </span>
      </div>
      <div className="ad-rfig" title={INVOICED}>
        <span className="ad-rfig__label">Invoiced by Ademero</span>
        <strong className="ad-rfig__value">{f.money(money.invoiced)}</strong>
        <span className="ad-rfig__note">not paid until marked paid</span>
      </div>
    </div>
  );
}

function PartnerHead(): ReactNode {
  return (
    <thead>
      <tr>
        <th scope="col">Partner</th>
        <th scope="col" className="is-numeric">
          Invoices paid
        </th>
        <th scope="col" className="is-numeric">
          Paid
        </th>
        <th scope="col" className="is-numeric">
          Invoiced by Ademero
        </th>
      </tr>
    </thead>
  );
}

/** Each partner with money in the period, the most first; a period with none says so. */
function ByPartner(p: {
  money: PartnerInvoices;
  period: AdminPeriod;
  render: RenderPartner;
}): ReactNode {
  const f = useFormat();
  if (p.money.partners.length === 0)
    return (
      <p className="ad-rtable__none">No partner paid or was invoiced, {periodLabel(p.period)}.</p>
    );
  return (
    <div className="ad-rtable__scroll">
      <table className="ad-sort">
        <PartnerHead />
        <tbody>
          {p.money.partners.map((row) => (
            <tr key={row.zohoAccountId}>
              <td>{p.render(row.zohoAccountId, row.name)}</td>
              <td className="is-numeric">{f.int(row.invoices)}</td>
              <td className="is-numeric">{f.money(row.net)}</td>
              <td className="is-numeric">{row.invoiced > 0 ? f.money(row.invoiced) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The last twelve months, newest first, as yyyy-MM. */
function lastMonths(asOf: string): string[] {
  const d = new Date(Date.parse(asOf));
  return Array.from({ length: 12 }, (_, i) => {
    const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
    return m.toISOString().slice(0, 7);
  });
}

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** A month's QuickBooks import: every account's issued statements of that month, one file. */
function MonthCsv({ asOf, href }: { asOf: string; href: (month: string) => string }): ReactNode {
  const months = lastMonths(asOf);
  const [month, setMonth] = useState(months[1] ?? months[0] ?? '');
  return (
    <div
      className="ad-row"
      style={{ alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 'var(--paige-space-3)' }}
    >
      <Field label="Month for QuickBooks">
        {({ id }) => (
          <Select id={id} value={month} onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => (
              <option key={m} value={m}>
                {MONTH.format(Date.parse(`${m}-01T00:00:00Z`))}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Button asChild variant="secondary">
        <a href={href(month)} download>
          Download statements CSV
        </a>
      </Button>
    </div>
  );
}

/**
 * Partner invoices: what partners paid in the period, apart from the companies' card money, against the period
 * before; what Ademero invoiced on its statements; by partner, each linked to its partner account page.
 */
export function PartnerInvoicesCard(p: {
  revenue: Revenue;
  choice: PeriodChoice;
  renderPartner?: RenderPartner | undefined;
  statementsCsv?: ((month: string) => string) | undefined;
}): ReactNode {
  const { partnerInvoices: money, period } = p.revenue;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{tileLabel('Partner invoices', p.choice, period)}</CardTitle>
      </CardHeader>
      <Figures money={money} days={period.days} />
      <ByPartner money={money} period={period} render={p.renderPartner ?? ((_z, name) => name)} />
      {p.statementsCsv ? <MonthCsv asOf={p.revenue.asOf} href={p.statementsCsv} /> : null}
    </Card>
  );
}
